import type { Path, UseFormReturn } from 'react-hook-form';
import { Labeled, Radio, RadioGroup, Stack, Switch } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IGeneralSettingsFormValues } from './GeneralSettingsForm';
import { FormCheckableDisclosure } from '~/components/atoms/fields/CheckableDisclosure';
import { FormField } from '~/components/atoms/FormField';

export type IGeneralSettingsFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IGeneralSettingsFormValues>;
  submittingFields?: Array<Path<IGeneralSettingsFormValues>>;
  onChange?: (
    form: UseFormReturn<IGeneralSettingsFormValues>,
    updatedFields?: Array<Path<IGeneralSettingsFormValues>>,
  ) => void;
};

const styles = stylex.create({
  radioSection: {
    marginLeft: 'calc(52px + 16px)',
  },
});

export const GeneralSettingsFormUsersSection: React.FC<
  IGeneralSettingsFormMainSectionProps
> = ({ sx, form, submittingFields, onChange }) => {
  const { t } = useTranslation();

  return (
    <FormCheckableDisclosure
      sx={sx}
      label={t('GeneralSettingsFormUsersSection.title')}
      supportingText={t('GeneralSettingsFormUsersSection.headline')}
      control={form.control}
      name="usersEnabled"
      initiallyExpanded={form.watch('usersEnabled')}
      rules={{
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.value) {
            form.setValue('registerWithCrowdaa', true);
            form.setValue('signInWithCrowdaa', true);

            onChange?.(form, [
              'usersEnabled',
              'registerWithCrowdaa',
              'signInWithCrowdaa',
            ]);
          } else {
            form.setValue('registerWithCrowdaa', false);
            form.setValue('signInWithCrowdaa', false);
            form.setValue('loginWithUsername', 'false');
            form.setValue('signInWithFacebook', false);
            form.setValue('signInWithApple', false);
            form.setValue('geolocation', false);

            onChange?.(form, [
              'usersEnabled',
              'registerWithCrowdaa',
              'signInWithCrowdaa',
              'loginWithUsername',
              'signInWithFacebook',
              'signInWithApple',
              'geolocation',
            ]);
          }
        },
      }}
      loading={submittingFields?.includes('usersEnabled')}
      switchable
    >
      <Labeled
        label={t(
          'GeneralSettingsFormUsersSection.field.registerWithCrowdaa.label',
        )}
        supportingText={t(
          'GeneralSettingsFormUsersSection.field.registerWithCrowdaa.supportingText',
        )}
        labelPosition="right"
      >
        <FormField
          name="registerWithCrowdaa"
          control={form.control}
          as={Switch}
          rules={{
            onChange: () => onChange?.(form, ['registerWithCrowdaa']),
          }}
          loading={submittingFields?.includes('registerWithCrowdaa')}
        />
      </Labeled>
      <Stack gap={2}>
        <Labeled
          label={t(
            'GeneralSettingsFormUsersSection.field.signInWithCrowdaa.label',
          )}
          labelPosition="right"
        >
          <FormField
            name="signInWithCrowdaa"
            control={form.control}
            as={Switch}
            rules={{
              onChange: () => onChange?.(form, ['signInWithCrowdaa']),
            }}
            loading={submittingFields?.includes('signInWithCrowdaa')}
          />
        </Labeled>
        <FormField
          as={RadioGroup}
          control={form.control}
          name="loginWithUsername"
          rules={{
            onChange: () => onChange?.(form, ['loginWithUsername']),
          }}
          disabled={!form.getValues('signInWithCrowdaa')}
        >
          <Stack gap={3} sx={styles.radioSection}>
            <Labeled
              label={t(
                'GeneralSettingsFormUsersSection.field.signInWithCrowdaa.option.emailAddress',
              )}
              labelPosition="right"
            >
              {(elementProps) => (
                <Radio
                  value="false"
                  loading={
                    submittingFields?.includes('loginWithUsername') &&
                    form.getValues('loginWithUsername') === 'false'
                  }
                  {...elementProps}
                />
              )}
            </Labeled>
            <Labeled
              label={t(
                'GeneralSettingsFormUsersSection.field.signInWithCrowdaa.option.username',
              )}
              labelPosition="right"
            >
              {(elementProps) => (
                <Radio
                  value="true"
                  loading={
                    submittingFields?.includes('loginWithUsername') &&
                    form.getValues('loginWithUsername') === 'true'
                  }
                  {...elementProps}
                />
              )}
            </Labeled>
          </Stack>
        </FormField>
      </Stack>
      <Labeled
        label={t(
          'GeneralSettingsFormUsersSection.field.signInWithFacebook.label',
        )}
        labelPosition="right"
        disabled={!form.watch('signInWithFacebookEnabled')}
      >
        <FormField
          name="signInWithFacebook"
          control={form.control}
          as={Switch}
          rules={{
            onChange: () => onChange?.(form, ['signInWithFacebook']),
          }}
          loading={submittingFields?.includes('signInWithFacebook')}
        />
      </Labeled>
      <Labeled
        label={t('GeneralSettingsFormUsersSection.field.signInWithApple.label')}
        labelPosition="right"
        disabled={!form.watch('signInWithAppleEnabled')}
      >
        <FormField
          name="signInWithApple"
          control={form.control}
          as={Switch}
          rules={{
            onChange: () => onChange?.(form, ['signInWithApple']),
          }}
          loading={submittingFields?.includes('signInWithApple')}
        />
      </Labeled>
      <Labeled
        label={t('GeneralSettingsFormUsersSection.field.geolocation.label')}
        supportingText={t(
          'GeneralSettingsFormUsersSection.field.geolocation.supportingText',
        )}
        labelPosition="right"
      >
        <FormField
          name="geolocation"
          control={form.control}
          as={Switch}
          rules={{
            onChange: () => {
              onChange?.(form, ['geolocation']);
            },
          }}
          loading={submittingFields?.includes('geolocation')}
        />
      </Labeled>
    </FormCheckableDisclosure>
  );
};
